import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { NotificationSetting } from "@jilian/shared";
import { useTheme } from "../../lib/theme";
import {
  getSettings,
  loadNotificationSetting,
  saveNotificationSetting,
  saveTargetTrainingDaysPerWeek,
} from "../../db/repo";
import { Button, Card, SectionTitle, Segmented, Toggle } from "../../components/ui";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** 固定训练提醒（阶段 6，§21）：设置持久化 + 通知权限 + 推送订阅渐进增强 + 页面内降级说明。 */
export function ReminderSection() {
  const [setting, setSetting] = useState<NotificationSetting | null>(null);
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">("unsupported");
  const [subStatus, setSubStatus] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    void loadNotificationSetting().then((s) => {
      if (mounted) setSetting(s);
    });
    const perm = "Notification" in window ? Notification.permission : "unsupported";
    setPermission(perm);
    return () => {
      mounted = false;
    };
  }, []);

  const update = (patch: Partial<NotificationSetting>) => {
    if (!setting) return;
    const next = { ...setting, ...patch };
    setSetting(next);
    void saveNotificationSetting(next);
  };

  const requestPermission = async () => {
    if (!("Notification" in window)) {
      setPermission("unsupported");
      return;
    }
    const p = await Notification.requestPermission();
    setPermission(p);
    if (p === "granted") setSubStatus("通知已授权（推送订阅需服务端部署后生效）。");
  };

  /** 推送订阅（渐进增强）：需要 VITE_VAPID_PUBLIC_KEY + 权限 + SW；任一缺失 → 如实降级。 */
  const subscribePush = async () => {
    const vapid = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
    if (!vapid) {
      setSubStatus("未配置推送公钥（VITE_VAPID_PUBLIC_KEY）：使用页面内提醒（打开应用时到点提示）。");
      return;
    }
    if (permission !== "granted") {
      setSubStatus("请先允许通知权限。");
      return;
    }
    if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) {
      setSubStatus("Service Worker 尚未就绪，稍后重试。");
      return;
    }
    try {
      const appSettings = await getSettings();
      const currentUserId = appSettings?.profile.id ?? "user-default";
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid) as unknown as BufferSource,
      });
      const { enqueueSyncEvent } = await import("../../db/repo");
      await enqueueSyncEvent({
        userId: currentUserId,
        entityType: "device",
        entityId: "device-local-1",
        operation: "upsert",
        entityVersion: 2,
        payload: {
          id: "device-local-1",
          userId: currentUserId,
          deviceName: "本设备",
          platform: "web",
          pushSubscription: sub.toJSON(),
          isPrimaryNotificationDevice: true,
          lastSeenAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
        },
      });
      setSubStatus("已订阅推送（当前设备设为主提醒设备；iOS PWA 后台提醒不可靠，页面内提醒始终可用）。");
    } catch (e) {
      setSubStatus(`订阅失败：${e instanceof Error ? e.message : "未知错误"}（页面内提醒仍可用）。`);
    }
  };

  return (
    <div className="border-t border-tone-border pt-3 space-y-3">
      <div className="text-[13px] font-medium text-tone-ink">训练固定提醒</div>
      <Toggle checked={Boolean(setting?.enabled)} onChange={(v) => update({ enabled: v })} label="开启" />
      {setting?.enabled ? (
        <>
          <div className="flex items-center justify-between">
            <span className="text-[14px] text-tone-ink">提醒时间</span>
            <input
              type="time"
              value={setting.fixedTime}
              onChange={(e) => update({ fixedTime: e.target.value })}
              className="min-h-[44px] px-3 rounded-xl bg-tone-border text-tone-ink"
            />
          </div>
          <div className="flex items-center justify-between text-[12px] text-tone-soft">
            <span>通知权限</span>
            <span>{permission === "granted" ? "已允许" : permission === "denied" ? "已拒绝（页面内提醒）" : permission === "default" ? "未决定" : "浏览器不支持"}</span>
          </div>
          {permission !== "granted" && permission !== "unsupported" ? (
            <Button variant="secondary" className="w-full" onClick={() => void requestPermission()}>
              允许通知
            </Button>
          ) : null}
          <Button variant="ghost" className="w-full" onClick={() => void subscribePush()}>
            订阅本设备推送
          </Button>
          {subStatus ? <p className="text-[12px] text-tone-soft">{subStatus}</p> : null}
          <p className="text-[11px] text-tone-soft leading-relaxed">
            平台限制（docs/03）：iPhone PWA 后台通知/后台计时不可靠；系统**不承诺准时**，始终回退为页面内提醒；打卡不制造压力（休息日按计划恢复也算完成）。
          </p>
        </>
      ) : null}
    </div>
  );
}

export function SettingsTraining() {
  const nav = useNavigate();
  const { toggle, isDark } = useTheme();
  const [rirMode, setRirMode] = useState<"label" | "number">("label");
  const [failurePref, setFailurePref] = useState<"enabled" | "guarded">("guarded");
  const [targetDays, setTargetDays] = useState<number>(4);

  useEffect(() => {
    void getSettings().then((s) => {
      if (s?.targetTrainingDaysPerWeek) {
        setTargetDays(s.targetTrainingDaysPerWeek);
      }
    });
  }, []);

  const handleTargetDaysChange = (days: number) => {
    setTargetDays(days);
    void saveTargetTrainingDaysPerWeek(days);
  };

  return (
    <div className="min-h-screen mx-auto max-w-[480px] px-4 pt-safe-top pb-10">
      {/* 顶部返回导航栏 */}
      <div className="flex items-center justify-between py-3 mb-2">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-tone-accent font-semibold text-sm active:opacity-70"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          返回
        </button>
        <h1 className="text-[17px] font-bold text-tone-ink">训练设置</h1>
        <div className="w-12" />
      </div>

      {/* 目标出勤 */}
      <SectionTitle>每周出勤目标</SectionTitle>
      <Card className="p-4 mb-4 border border-tone-border shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[15px] font-bold text-tone-ink">每周目标训练天数</div>
            <div className="text-[12px] text-tone-soft mt-0.5">
              用于周报出勤率统计与疲劳评估（循证推荐 3–5 天）
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleTargetDaysChange(Math.max(2, targetDays - 1))}
              className="w-8 h-8 rounded-elem bg-black/5 dark:bg-white/10 font-bold text-base active:scale-90"
            >
              −
            </button>
            <span className="text-[16px] font-bold tabular-nums w-8 text-center text-tone-ink font-display">
              {targetDays} 天
            </span>
            <button
              type="button"
              onClick={() => handleTargetDaysChange(Math.min(6, targetDays + 1))}
              className="w-8 h-8 rounded-elem bg-black/5 dark:bg-white/10 font-bold text-base active:scale-90"
            >
              +
            </button>
          </div>
        </div>
      </Card>

      {/* 序列计划入口 */}
      <SectionTitle>训练序列与分化</SectionTitle>
      <Card
        className="p-4 mb-4 cursor-pointer hover:border-tone-accent/40 transition-all border border-tone-border shadow-sm"
        onClick={() => nav("/plan-editor")}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[15px] font-bold text-tone-ink">自定义训练序列模板</div>
            <div className="text-[12px] text-tone-soft mt-0.5">
              编辑 A–E 模板分化、目标肌群、首选动作与简单模式生成
            </div>
          </div>
          <span className="text-tone-soft text-lg">›</span>
        </div>
      </Card>

      {/* 训练偏好 */}
      <SectionTitle>训练记录偏好</SectionTitle>
      <Card className="p-4 mb-4 space-y-4 border border-tone-border shadow-sm">
        <div>
          <div className="text-[13px] text-tone-soft mb-1.5 font-medium">RIR 显示方式</div>
          <Segmented
            value={rirMode}
            onChange={setRirMode}
            options={[
              { value: "label", label: "标签（很轻松/合适…）" },
              { value: "number", label: "数值（0–4）" },
            ]}
          />
        </div>

        <div>
          <div className="text-[13px] text-tone-soft mb-1.5 font-medium">力竭偏好（孤立/稳定器械最后组）</div>
          <Segmented
            value={failurePref}
            onChange={setFailurePref}
            options={[
              { value: "enabled", label: "允许接近力竭" },
              { value: "guarded", label: "放宽，保留 RIR 2" },
            ]}
          />
        </div>

        <ReminderSection />

        <div className="flex items-center justify-between pt-2 border-t border-tone-border">
          <span className="text-[15px] text-tone-ink font-medium">界面外观</span>
          <button
            onClick={toggle}
            className="min-h-[38px] px-4 rounded-xl bg-black/5 dark:bg-white/10 text-tone-ink text-[13px] font-semibold active:scale-95"
          >
            {isDark ? "深色模式" : "浅色模式"}
          </button>
        </div>
      </Card>
    </div>
  );
}
